import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { Trans, useTranslation } from "react-i18next";
import { Footer, Header, Page, PageContent } from "@/components/page.tsx";
import {
  Modal,
  ModalContent,
  ModalTitle,
  useMuiModal,
} from "@/components/modal.tsx";
import { Checkbox } from "@/components/ui/checkbox.tsx";
import { Button } from "@/components/ui/button.tsx";
import { Label } from "@/components/ui/label.tsx";
import { XIcon } from "lucide-react";
import { ReactNode, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  type Contract as RegistrationContract,
  useContractsForRegistration,
} from "@frontend-common/hooks/use-contracts-for-registration";
import { cn, resetKiosk } from "@/lib/utils";
import { useNetworkStatus } from "@/hooks/use-network-status";
import { NotAvailableInOfflineModeModal } from "@/components/not-available-in-offline-mode-modal";
import { getBaseUrl, getServerUrl } from "@/lib/server";
import { Popup, usePopup } from "@/components/popup";
import { OfflineWarning } from "@/components/offline-warning";
import { AudioSlot, useAudioSlot } from "@/features/audio/kiosk-audio";

export const Route = createFileRoute("/login-option-selector-screen")({
  component: RouteComponent,
  beforeLoad: async () => {
    await resetKiosk({ preserveTracking: true });
  },
});

function RouteComponent() {
  useAudioSlot("page.login-option-selector");
  useTranslation();
  const { showModal, closeModal } = useMuiModal();
  const networkStatus = useNetworkStatus();
  const popup = usePopup("unregistered_users");

  return (
    <Page className={`bg-background`}>
      <OfflineWarning />
      <Header backArrowHref={`/start-screen`} />
      <PageContent>
        <h2 className={`font-helvetica font-bold text-6xl text-center mb-16`}>
          <Trans>Regisztrált felhasználó vagy?</Trans>
        </h2>
        <div
          className={`grid grid-cols-2 gap-10 items-center justify-center mb-16`}
        >
          <Link
            to={`/login-screen`}
            className={`border-4 rounded-2xl border-bub-vivid-blue shadow-lg px-18 py-14 text-[38px] leading-12 text-bub-vivid-blue w-full text-center`}
          >
            <Trans>Igen, belépek</Trans>
          </Link>
          <div className="relative">
            <Link
              disabled={networkStatus.data === "disconnected"}
              onClick={() => {
                if (networkStatus.data === "disconnected") {
                  showModal(<NotAvailableInOfflineModeModal />);
                  return;
                }
              }}
              to={`/registration-screen`}
              className={cn(
                `flex items-center border-4 rounded-2xl bg-bub-vivid-blue border-bub-vivid-blue shadow-lg text-[38px] leading-12 text-white max-w-max px-12 py-2 text-center h-full`,
                networkStatus.data === "disconnected" && "opacity-50",
              )}
            >
              <Trans>Regisztrálok a kedvezményes árakért</Trans>
            </Link>
            {networkStatus.data === "disconnected" && (
              <span className="absolute inset-0 flex items-center justify-center pointer-events-none text-red-600 font-bold text-[100px] leading-none">
                ✕
              </span>
            )}
          </div>
        </div>
        <div className={`flex justify-center`}>
          <div
            onClick={async () => {
              const isDisconnected = networkStatus.data === "disconnected";
              const isRegisterLink =
                popup.data?.popupButtonBehaviour === "registration";
              if (popup.data?.image && !(isDisconnected && isRegisterLink)) {
                const hasNavigated = await new Promise(async (resolve) => {
                  await showModal(
                    <Popup
                      image={popup.data.image}
                      behavior={popup.data?.popupButtonBehaviour}
                      onClose={(navigated) => {
                        closeModal();
                        resolve(navigated);
                      }}
                    />,
                  );
                  resolve(false);
                });

                if (hasNavigated) {
                  return;
                }
              }
              showModal(<TermsModal />);
            }}
            className={`bg-bub-dark-blue text-white font-helvetica px-20 pt-6 pb-4 rounded-full text-3xl font-bold`}
          >
            <Trans>Nem, tovább</Trans>
          </div>
        </div>
      </PageContent>
      <Footer
        helpText={
          <Trans>
            Ha már regisztrált felhasználónk vagy, válaszd az "Igen, belépek"
            opciót. Amennyiben nem vagy regisztrált felhasználónk, válaszd a
            "Nem, tovább" opciót. Ha regisztrálnál most, válaszd a
            "Regisztrálok" gombot.
          </Trans>
        }
      />
    </Page>
  );
}

function TermsModal() {
  useAudioSlot("modal.terms");
  const [isChecked, setIsChecked] = useState<boolean>(false);
  const { closeModal } = useMuiModal();
  const { data: contractsData } = useContractsForRegistration();
  const documents =
    (
      contractsData as { documents?: RegistrationContract[] } | undefined
    )?.documents ?? [];
  const termsLink = documents.find(
    (x: RegistrationContract) => x.typeName === "terms",
  )?.link;
  const navigate = useNavigate();

  return (
    <Modal>
      <ModalTitle>
        <></>
      </ModalTitle>
      <ModalContent className={`px-12 pb-6`}>
        <p className={`text-bub-darker-blue`}>
          <Trans>
            Üdv a Bubbles önkiszolgáló mosodában! A szolgáltatás
            igénybevételéhez először kérjük, pipáld ki az alábbi mezőt!
          </Trans>
        </p>
        <ContractCheckbox
          checked={isChecked}
          onChange={setIsChecked}
          link={termsLink}
          label={<Trans>Elfogadom a szerződési feltételeket</Trans>}
        />
        <div className="flex gap-6">
          <Button
            onClick={closeModal}
            variant="outline"
            className={`px-3 py-6 w-[200px]`}
          >
            <Trans>Mégse</Trans>
          </Button>
          <Button
            disabled={!isChecked}
            onClick={() => {
              closeModal();
              navigate({
                to: "/machine-type-selector-screen",
              });
            }}
            className={`px-3 py-6 w-[200px]`}
          >
            <Trans>Elfogadom</Trans>
          </Button>
        </div>
      </ModalContent>
    </Modal>
  );
}

export function ContractCheckbox({
  checked,
  onChange,
  link,
  label,
}: {
  checked: boolean;
  onChange: (checked: boolean) => void;
  link: string | undefined;
  label: ReactNode;
}) {
  const baseUrl = getBaseUrl();
  const { showModal, closeModal } = useMuiModal();

  const iframeLink = useQuery({
    queryKey: ["iframe-link", link],
    queryFn: async () => {
      const urls = [`${baseUrl}${link}`, `${getServerUrl()}${link}`];
      for (const url of urls) {
        console.log("trying url... ", url, { baseUrl });
        try {
          const res = await fetch(url!, { method: "GET" });
          if (!res.ok) {
            throw new Error("Link down");
          }
          return url;
        } catch (e) {
          continue;
        }
      }
      return null;
    },
  });

  return (
    <div className="flex gap-4 ms-0 me-auto items-center mb-12">
      <Checkbox
        id="aszf"
        checked={checked}
        onCheckedChange={(checked) => {
          onChange(checked);
        }}
      />
      <div className="text-start">
        <Label htmlFor="aszf" className="text-md text-bub-darker-blue">
          {label}
        </Label>
        <br />
        <span
          className="font-bold font-helvetica underline text-bub-darker-blue text-[1.5rem] py-4"
          onClick={() =>
            showModal(
              <Modal>
                <AudioSlot slot="modal.contract-document" />
                <div className="absolute bg-white -top-[48vh] -left-[12vw] w-[85vw] p-6 rounded-lg">
                  <XIcon
                    onClick={closeModal}
                    className="absolute top-5 right-5 w-10 h-10"
                  />
                  <div>
                    <iframe
                      src={iframeLink.data ?? ""}
                      className="w-full h-[93svh]"
                    />
                  </div>
                </div>
              </Modal>,
            )
          }
        >
          <Trans>Itt olvashatod</Trans>
        </span>
      </div>
    </div>
  );
}
